import { useEffect, useState } from "react";

/**
 * PSeQ2XrButton — opens the PSEQ2 spatial app (XREAL xbx glasses, 3DoF).
 *
 * Usage, in the PSEQ2 page next to the report / share buttons:
 *   import PSeQ2XrButton from "./PSeQ2XrButton";
 *   <PSeQ2XrButton bassins={f} sun={w} lat={D?.[0]} lng={D?.[1]} place={q} />
 *
 * Props are optional: when given, the current simulation opens directly
 * in the glasses (same pond count, coverage and location).
 * Put the pseq2-xr.html file in the public/ folder of the site.
 */

const APP_PATH = "/pseq2-xr.html";
const APP_LANGS = ["fr", "en", "es"]; // languages the spatial app supports

const COPY = {
  fr: { title: "Mode XREAL", sub: "Votre simulation en 3D autour de vous", badge: "3DoF", hint: "Lunettes branchées : passez en plein écran depuis l'application." },
  en: { title: "XREAL mode", sub: "Your simulation in 3D around you", badge: "3DoF", hint: "With the glasses connected, go fullscreen from the app." },
  es: { title: "Modo XREAL", sub: "Su simulación en 3D a su alrededor", badge: "3DoF", hint: "Con las gafas conectadas, active pantalla completa en la aplicación." },
  pt: { title: "Modo XREAL", sub: "Sua simulação em 3D ao seu redor", badge: "3DoF", hint: "Com os óculos conectados, ative a tela cheia no aplicativo." },
  ru: { title: "Режим XREAL", sub: "Открыть симуляцию в 3D вокруг вас", badge: "3DoF", hint: "Подключив очки, включите полноэкранный режим в приложении." },
  ar: { title: "وضع XREAL", sub: "افتح المحاكاة ثلاثية الأبعاد من حولك", badge: "3DoF", hint: "بعد توصيل النظارات، فعّل وضع ملء الشاشة من التطبيق." },
  ja: { title: "XREALモード", sub: "シミュレーションを周囲に3Dで表示", badge: "3DoF", hint: "グラスを接続したら、アプリで全画面表示にしてください。" },
  zh: { title: "XREAL 模式", sub: "在您周围以 3D 打开模拟", badge: "3DoF", hint: "连接眼镜后，请在应用中切换为全屏。" },
};

function useSiteLang() {
  const read = () => (document.documentElement.lang || "en").slice(0, 2).toLowerCase();
  const [lang, setLang] = useState(read);
  useEffect(() => {
    const obs = new MutationObserver(() => setLang(read()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["lang"] });
    return () => obs.disconnect();
  }, []);
  return lang;
}

function buildHref({ lang, bassins, sun, lat, lng, place }) {
  const q = new URLSearchParams({ lang: APP_LANGS.includes(lang) ? lang : "en" });
  const h = new URLSearchParams();
  if (Number.isFinite(bassins)) h.set("p", String(Math.round(bassins)));
  if (sun === "full" || sun === "mid" || sun === "low") h.set("c", sun);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    h.set("lat", lat.toFixed(4));
    h.set("lng", lng.toFixed(4));
    if (place) h.set("n", place);
  }
  const hash = h.toString();
  return `${APP_PATH}?${q}${hash ? `#${hash}` : ""}`;
}

function GlassesIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M2 10.5h20" />
      <path d="M3 10.5v3a3 3 0 0 0 3 3h2.2a2.5 2.5 0 0 0 2.3-1.6l.5-1.4a1 1 0 0 1 1.9 0l.5 1.4a2.5 2.5 0 0 0 2.3 1.6H18a3 3 0 0 0 3-3v-3" />
      <path d="M2 10.5 3.5 7M22 10.5 20.5 7" />
    </svg>
  );
}

export default function PSeQ2XrButton({ bassins, sun, lat, lng, place, className = "" }) {
  const lang = useSiteLang();
  const t = COPY[lang] || COPY.en;
  const href = buildHref({ lang, bassins, sun, lat, lng, place });

  return (
    <div className={`w-full ${className}`}>
      <a
        href={href}
        data-testid="pseq2-xr-btn"
        className="group relative flex w-full items-center gap-3 overflow-hidden rounded-full border border-sky-300 bg-ink px-5 py-3 text-white shadow-[0_0_0_0_rgba(56,189,248,0.0)] transition-all duration-300 hover:border-sky-400 hover:shadow-[0_0_24px_-4px_rgba(56,189,248,0.55)] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2"
      >
        {/* scan line */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-sky-400/25 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-[400%]"
        />
        <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-sky-400/60 bg-sky-500/10 text-sky-300">
          <GlassesIcon className="h-5 w-5" />
        </span>
        <span className="relative min-w-0 flex-1 text-start">
          <span className="block font-headline text-sm font-bold leading-tight">{t.title}</span>
          <span className="block truncate text-xs leading-tight text-sky-200/80">{t.sub}</span>
        </span>
        <span className="relative shrink-0 rounded-full border border-sky-400/50 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-sky-300">
          {t.badge}
        </span>
      </a>
      <p className="mt-2 px-2 text-center font-mono text-[10px] leading-snug tracking-[0.04em] text-ink/50">
        {t.hint}
      </p>
    </div>
  );
}
